import { Draggable } from "@hello-pangea/dnd";
import { CalendarDays, CheckSquare } from "lucide-react";
import { useGetOne, useTranslate } from "ra-core";
import { Link } from "react-router";
import { cn } from "@/lib/utils";

import { findChoice, PRIORITIES } from "../common/constants";
import {
  isOverdue,
  SaleAvatar,
  StatusBadge,
  useFormatDate,
} from "../common/components";
import type { TeamTask } from "../types";

export const TeamTaskCard = ({
  task,
  index,
}: {
  task: TeamTask;
  index: number;
}) => (
  <Draggable draggableId={String(task.id)} index={index}>
    {(provided, snapshot) => (
      <div
        ref={provided.innerRef}
        {...provided.draggableProps}
        {...provided.dragHandleProps}
      >
        <TeamTaskCardContent task={task} dragging={snapshot.isDragging} />
      </div>
    )}
  </Draggable>
);

export const TeamTaskCardContent = ({
  task,
  dragging,
}: {
  task: TeamTask;
  dragging?: boolean;
}) => {
  const translate = useTranslate();
  const formatDate = useFormatDate();
  const checklist = task.checklist ?? [];
  const checklistDone = checklist.filter((item) => item.done).length;
  const overdue = isOverdue(task.due_date, task.status === "done");
  return (
    <Link
      to={`/team_tasks/${task.id}`}
      className={cn(
        "block rounded-xl border bg-card p-3 text-card-foreground shadow-card transition-shadow hover:shadow-md",
        dragging && "rotate-1 shadow-lg",
        task.status === "done" && "opacity-70",
      )}
    >
      <div className="flex items-start justify-between gap-2">
        <p
          className={cn(
            "text-sm font-medium leading-snug",
            task.status === "done" && "line-through",
          )}
        >
          {task.title}
        </p>
        <SaleAvatar saleId={task.assignee_id} />
      </div>
      {task.deal_id != null && <DealChip dealId={task.deal_id} />}
      <div className="mt-2 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
        {task.priority !== "medium" && (
          <StatusBadge choice={findChoice(PRIORITIES, task.priority)} />
        )}
        {task.due_date && (
          <span
            className={cn(
              "inline-flex items-center gap-1",
              overdue && "font-medium text-destructive",
            )}
          >
            <CalendarDays className="size-3" />
            {formatDate(task.due_date, { day: "numeric", month: "short" })}
          </span>
        )}
        {checklist.length > 0 && (
          <span
            className="inline-flex items-center gap-1"
            title={translate("hub.team_tasks.checklist_progress", {
              done: checklistDone,
              total: checklist.length,
            })}
          >
            <CheckSquare className="size-3" />
            {checklistDone}/{checklist.length}
          </span>
        )}
        {task.tags?.map((tag) => (
          <span key={tag} className="rounded-full bg-muted px-2 py-0.5">
            {tag}
          </span>
        ))}
      </div>
    </Link>
  );
};

const DealChip = ({ dealId }: { dealId: TeamTask["deal_id"] }) => {
  const { data: deal } = useGetOne("deals", {
    id: dealId as NonNullable<typeof dealId>,
  });
  if (!deal) return null;
  return (
    <p className="mt-1 truncate text-xs text-muted-foreground">↳ {deal.name}</p>
  );
};
